<template>
  <div class="row">
    <div class="col-12">
      <div class="custom-table rte-text">
        <CustomTable>
          <table style="width: 100%; min-width: 800px">
            <thead>
              <tr>
                <th scope="col">
                  {{
                    $textDictionary[
                      'downloadCenterSoftware.operationSystemTableLabel'
                    ] ?? 'downloadCenterSoftware.operationSystemTableLabel'
                  }}
                </th>
                <th scope="col">
                  {{
                    $textDictionary[
                      'downloadCenterSoftware.formatTableLabel'
                    ] ?? 'downloadCenterSoftware.formatTableLabel'
                  }}
                </th>
                <th v-if="hasFurtherDownloads" scope="col">
                  {{
                    $textDictionary[
                      'downloadCenterSoftware.furtherDownloadsTableLabel'
                    ] ?? 'downloadCenterSoftware.furtherDownloadsTableLabel'
                  }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="asset of softwareLinks" :key="asset.os.name">
                <tr>
                  <td class="nowrap" style="max-width: 500px">
                    <Button
                      v-if="!isLinkToAppStore(asset.platform) || !appStoreLogo"
                      class="custom-button-link"
                      style-type="link"
                      icon-type="icon-left"
                      :icon-name="
                        asset.os.icon === 'macOs' ? 'mac' : asset.os.icon
                      "
                      @click="emits('selectDownload', asset)"
                    >
                      {{ asset.os.name }}
                    </Button>
                    <div
                      v-else-if="appStoreLogo"
                      @click="emits('selectDownload', asset)"
                    >
                      <media
                        :media="appStoreLogo"
                        class="app-store-image"
                        :lazyload="true"
                      />
                    </div>
                  </td>
                  <td class="nowrap">
                    <Button
                      v-if="asset.format.fileExtension"
                      style-type="link"
                      class="custom-button-link"
                      icon-type="icon-left"
                      :icon-name="asset.format.icon"
                      @click="emits('selectDownload', asset)"
                    >
                      {{ asset.format.fileExtension }} </Button
                    >{{
                      asset.format.readableFilesize &&
                      asset.format.fileExtension
                        ? ', ' + asset.format.readableFilesize
                        : asset.format.readableFilesize
                    }}
                  </td>
                  <td v-if="hasFurtherDownloads">
                    <template
                      v-for="singleDownload of asset.furtherDownloads"
                      :key="singleDownload.label"
                    >
                      <Button
                        style-type="link"
                        class="custom-button-link"
                        icon-type="icon-left"
                        :icon-name="asset.format.icon"
                        @click="
                          emits(
                            'forceDownloadAndShowSuccess',
                            singleDownload.uri,
                            singleDownload.label ?? 'no_label_found'
                          )
                        "
                      >
                        {{ singleDownload.label }}
                      </Button>
                      <br />
                    </template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </CustomTable>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Button,
  Media,
  Table as CustomTable
} from '@hypercodestudio/basler-components';
import type { MediaItemInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/Media.vue';

const { $textDictionary, $globalPageSettings } = useNuxtApp();
const APP_STORE_PLATFORM = 'appstore';

const emits = defineEmits<{
  selectDownload: [asset: SoftwareLinks];
  forceDownloadAndShowSuccess: [uri: string, softwareName: string];
}>();

type SoftwareLinks = {
  formRequired: boolean;
  os: {
    icon?: string;
    name: string;
    nameEN: string;
    uri?: string;
  };
  format: {
    icon: 'download';
    fileExtension?: string;
    readableFilesize?: string;
    uri: string;
  };
  platform?: string[];
  furtherDownloads: FurtherDownloads[];
};

type FurtherDownloads = {
  label?: string;
  labelEN?: string;
  uri: string;
};

interface Props {
  softwareLinks: SoftwareLinks[];
  softwareId: string;
}

const props = defineProps<Props>();
const appStoreLogo: MediaItemInterface = {
  type: 'image',
  content: {
    src: $globalPageSettings.value?.appStoreLogo?.url
      ? $globalPageSettings.value?.appStoreLogo?.url +
        '?fm=webp&f=center&w=600&h=250&q=100&fit=pad'
      : '',
    alt:
      $globalPageSettings.value?.appStoreLogo?.title ??
      'Download on the App Store'
  }
};
const hasFurtherDownloads = computed(() =>
  props.softwareLinks.some((asset) => asset.furtherDownloads.length > 0)
);

function isLinkToAppStore(platform: string[] | undefined): boolean {
  return platform?.includes(APP_STORE_PLATFORM) ?? false;
}
</script>

<style>
.custom-button-link {
  .button__content {
    font-family: var(--font-family-body);
    font-weight: var(--font-weight-body);
    text-decoration: underline;
    padding: 0;
    text-wrap: wrap;
    text-align: left;
  }
}
.app-store-image img {
  width: 200px;
}
</style>
<style scoped>
.app-store-image {
  cursor: pointer;
}
</style>
